import { Alert, Button, Modal, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { AllyAdmin } from '@goelectric/shared-types';

/**
 * El contrato entrega `inviteUrl` **relativa** (`/invite?token=…`). Para que el link
 * copiado sea compartible se arma la absoluta contra el origen de la app.
 */
export function toAbsoluteInviteUrl(inviteUrl: string): string {
  return new URL(inviteUrl, window.location.origin).toString();
}

/** Admin con su link de enrolamiento garantizado (solo lo devuelve el POST de alta/agregado/reenroll). */
export type InvitedAdmin = AllyAdmin & { inviteUrl: string };

/** Columnas de la tabla: cada link con su botón de copiar (absoluto y compartible). */
const INVITE_COLUMNS: ColumnsType<InvitedAdmin> = [
  {
    title: 'Nombre y apellidos',
    key: 'name',
    render: (_, admin) => admin.name ?? '—',
  },
  { title: 'Email', dataIndex: 'email', key: 'email' },
  {
    title: 'Link de invitación',
    key: 'inviteUrl',
    render: (_, admin) => (
      <Typography.Text
        copyable={{ text: toAbsoluteInviteUrl(admin.inviteUrl) }}
        code
        className="aliados-invite-modal__link"
      >
        {toAbsoluteInviteUrl(admin.inviteUrl)}
      </Typography.Text>
    ),
  },
];

interface InviteLinksModalProps {
  open: boolean;
  admins: InvitedAdmin[];
  onClose: () => void;
  /** Titular focal. Por defecto el storytelling del alta. */
  title?: string;
  /** Párrafo de entrada. Por defecto el del alta. */
  lead?: string;
  /** Texto del botón de cierre. */
  footerLabel?: string;
}

/**
 * Modal de links de enrolamiento. Es el componente que nace el alta (TG-118) y que el
 * detalle reusa para "agregar administradores" y "regenerar invitación" (F11): mismo
 * `toAbsoluteInviteUrl`, misma tabla y mismo "copiar todos". El titular y el párrafo se
 * pueden contextualizar por caso; el resto es idéntico.
 *
 * No hay proveedor de correo: este es el único momento en que el link se puede obtener.
 */
export function InviteLinksModal({
  open,
  admins,
  onClose,
  title = 'Tenemos un nuevo aliado',
  lead = 'A los administradores autorizados de nuestro nuevo aliado hay que enviarles el link para que realicen su primer ingreso.',
  footerLabel = 'Cerrar',
}: InviteLinksModalProps) {
  const allInviteUrls = admins.map((admin) => toAbsoluteInviteUrl(admin.inviteUrl));

  return (
    <Modal
      open={open}
      rootClassName="aliados-invite-modal"
      title={
        <Typography.Title level={3} className="aliados-invite-modal__headline" style={{ margin: 0 }}>
          {title}
        </Typography.Title>
      }
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {footerLabel}
        </Button>
      }
      width={640}
    >
      <Typography.Paragraph className="aliados-invite-modal__lead">{lead}</Typography.Paragraph>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 20 }}
        message="Cada link tiene vigencia de tan solo 24 horas a partir de este momento."
      />

      <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
        Administradores autorizados
      </Typography.Title>
      <Table<InvitedAdmin>
        rowKey="userId"
        size="small"
        columns={INVITE_COLUMNS}
        dataSource={admins}
        pagination={false}
      />

      {allInviteUrls.length > 1 && (
        <Typography.Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
          <Typography.Text copyable={{ text: allInviteUrls.join('\n') }}>
            Copiar todos los links
          </Typography.Text>
        </Typography.Paragraph>
      )}
    </Modal>
  );
}
